import React from "react";
import { Check, Clock, HandCoins, Handshake, Flag } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import SocialOutreachButtons from "@/components/SocialOutreachButtons";
import PendingSocialsBadge from "@/components/PendingSocialsBadge";
import { TopPerformerBadge } from "@/components/realtors/RealtorBadges";
import PhotoAvatar from "@/components/realtors/PhotoAvatar";
import QueueRankChip from "./QueueRankChip";
import HoldCountdownBadge from "./HoldCountdownBadge";
import NeedsFollowUpBadge from "./NeedsFollowUpBadge";
import { nameOf, formatCurrency, formatRelative } from "./utils";

// Desktop-only <tr> version — used inside the sortable+resizable table.
// Same data as OutreachRow but rendered as table cells so column widths
// stay in sync with the <colgroup> defined in the parent.
export default function OutreachTableRow({
  row, selected, queueRank, held, holdExpiresAt, onSocialInteract,
  onToggle, onContactLogged,
  onFollowChanged, onLinkRemoved, onSocialVerified, onView, onPatch,
  onBrokerageClick,
  currency = "CAD", platformOrder,
}) {
  const name = nameOf(row);
  const lastTouchLabel = row.last_contact_at || row.latest_touch_at
    ? formatRelative(row.last_contact_at || row.latest_touch_at)
    : "Never";
  const refCount = Number(row.referral_count) || 0;
  const revenue = Number(row.referral_revenue_total) || 0;
  return (
    <tr
      className={
        "hover:bg-slate-50 transition-colors align-middle" +
        (held
          ? " bg-sky-50 ring-1 ring-inset ring-sky-200"
          : queueRank ? " bg-amber-50/40" : "")
      }
      data-testid={`outreach-row-${row.id}`}
    >
      <td className="px-3 py-3">
        <Checkbox
          checked={selected}
          onCheckedChange={onToggle}
          data-testid={`outreach-select-${row.id}`}
        />
      </td>
      <td className="px-3 py-3 min-w-0">
        <div className="flex items-start gap-2.5 min-w-0">
          <PhotoAvatar realtor={row} size="sm" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              {held && holdExpiresAt ? (
                <HoldCountdownBadge expiresAt={holdExpiresAt} id={row.id} />
              ) : (
                <QueueRankChip rank={queueRank} id={row.id} />
              )}
              {row.flagged_at ? (
                <Flag
                  className="h-3.5 w-3.5 text-rose-600 fill-rose-600 shrink-0"
                  aria-label="Flagged for follow-up"
                  data-testid={`flag-badge-${row.id}`}
                />
              ) : null}
              <button
                type="button"
                onClick={onView}
                className="text-sm font-semibold text-slate-900 hover:underline truncate text-left min-w-0"
                data-testid={`outreach-name-${row.id}`}
              >
                {name}
              </button>
              <TopPerformerBadge realtor={row} compact />
              <NeedsFollowUpBadge row={row} />
            </div>
            {row.met_in_person_at ? (
              <Badge variant="outline" className="text-[10px] mt-1 gap-1 bg-emerald-50 border-emerald-200 text-emerald-700">
                <Check className="h-3 w-3" /> Met in person
              </Badge>
            ) : null}
          </div>
        </div>
      </td>
      <td className="px-3 py-3 text-xs text-slate-600 truncate">
        {row.brokerage_id ? (
          <button
            type="button"
            onClick={() => onBrokerageClick?.(row.brokerage_id)}
            className="text-left truncate hover:underline hover:text-slate-900 transition-colors"
            data-testid={`outreach-brokerage-${row.id}`}
          >
            {row.brokerage_name || "—"}
          </button>
        ) : (
          row.brokerage_name || "—"
        )}
      </td>
      <td className="px-3 py-3" onClickCapture={onSocialInteract}>
        <div className="flex flex-col gap-1">
          <SocialOutreachButtons
            realtor={row}
            onContactLogged={onContactLogged}
            onFollowChanged={onFollowChanged}
            onLinkRemoved={onLinkRemoved}
            onSocialVerified={onSocialVerified}
            platformOrder={platformOrder}
            compact
          />
          <PendingSocialsBadge realtor={row} onChange={onPatch} onFollowChanged={onFollowChanged} compact />
        </div>
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-1.5 text-xs text-slate-600">
          {row.last_contact_at || row.latest_touch_at ? (
            <Clock className="h-3 w-3 text-slate-400" />
          ) : (
            <span className="h-2 w-2 rounded-full bg-slate-300" aria-hidden="true" />
          )}
          <span className="truncate">{lastTouchLabel}</span>
        </div>
      </td>
      <td className="px-3 py-3 text-center text-xs tabular-nums" data-testid={`outreach-referrals-${row.id}`}>
        {refCount > 0 ? (
          <span className="inline-flex items-center gap-1 text-rose-700 font-semibold">
            <Handshake className="h-3.5 w-3.5" />
            {refCount}
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-3 py-3 text-right text-xs tabular-nums" data-testid={`outreach-revenue-${row.id}`}>
        {revenue > 0 ? (
          <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
            <HandCoins className="h-3.5 w-3.5" />
            {formatCurrency(revenue, currency)}
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-3 py-3 text-center text-xs tabular-nums text-slate-600">
        {row.touch_count > 0 ? row.touch_count : "—"}
      </td>
    </tr>
  );
}
